import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { captureLabelmapParts, planLabelmapExport } from '@/src/segmentation/io/composition';
import { allocateLabelmap, labelmapScalars } from '@/src/segmentation/io/labelmap';
import { writeMaskInto } from '@/src/segmentation/masks/overlap';
import { writeImage } from '@/src/io/readWriteImage';
import { buildSegNrrdMetadata } from '@/src/io/segNrrdMetadata';
import type { DentalInstance } from './types';

export function assertSameGrid(parent: vtkImageData, label: vtkImageData) {
  if (parent.getDimensions().some((n, i) => n !== label.getDimensions()[i])) throw new Error('Label dimensions differ from the source.');
  for (const ijk of [[0, 0, 0], ...[0, 1, 2].map(axis => [0, 1, 2].map(i => i === axis ? parent.getDimensions()[i] - 1 : 0))]) {
    const point = ijk as [number, number, number];
    const a = parent.indexToWorld(point); const b = label.indexToWorld(point);
    if (a.some((n, i) => Math.abs(n - b[i]) > 1e-4)) throw new Error('Label affine differs from the source; no resampling was applied.');
  }
}
/** Preserve the source's label values instead of upstream's sequential bins. */
export async function exportVolumeLabels(imageId: string, valuesBySegment: Map<string, number>, format = 'nii.gz', instances: DentalInstance[] = []) {
  const source = useImageCacheStore().getVtkImageData(imageId);
  if (!source) throw new Error('Source volume is not loaded.');
  const plan = planLabelmapExport(imageId);
  if (plan.hasOverlap || plan.exceedsCapacity) throw new Error('Overlapping labels cannot be exported as one NIfTI; resolve overlap before saving.');
  const registry = useSegmentStore().segments;
  const ordered = [...useSegmentationStore().imageMasks(imageId)].sort((a, b) => registry.orderIndexOf(a.segmentId) - registry.orderIndexOf(b.segmentId));
  const snapshot = captureLabelmapParts(imageId, [ordered]);
  snapshot.parts[0].forEach((part, i) => {
    const value = valuesBySegment.get(ordered[i].segmentId);
    if (!value) throw new Error('An editable mask has lost its persistent label identity.');
    part.descriptor.value = value;
  });
  const labelmap = allocateLabelmap(snapshot.parent, Math.max(0, ...valuesBySegment.values()));
  try {
    const scalars = labelmapScalars(labelmap);
    for (const part of snapshot.parts[0]) if (part.bounded) writeMaskInto(scalars, labelmap.getDimensions(), part.bounded, part.descriptor.value);
    const metadata = format === 'seg.nrrd' ? buildSegNrrdMetadata(instances.map(row => ({ ...row, value: row.labelValue })), labelmap.getDimensions() as [number, number, number]) : undefined;
    // Persistent UUIDs are the exported Slicer segment IDs; label values are
    // preserved independently, including sparse values such as 60000.
    instances.forEach((row, i) => metadata?.set(`Segment${i}_ID`, row.id));
    return new Blob([await writeImage(format, labelmap, metadata)], { type: 'application/octet-stream' });
  } finally { labelmap.delete(); snapshot.parent.delete(); }
}
